/**
 * E63.1 — the canvas→engine surface conversion, extracted pure and DOM-free
 * so the "preview equals engine" contract is TESTED on the real code path.
 *
 * The defect: this conversion (then inside SiteCanvas) hardcoded a 35° tilt
 * literal — harmless before E55, but once the tilt correction became
 * unconditional the browser preview and the API computed DIFFERENT module
 * counts (+13.2 % at 22°, −13.6 % at 45°): the Design toolbar disagreed with
 * the simulation and every generated document, while the file header still
 * asserted they cannot diverge. Every physical surface property now comes
 * from the surface itself — no literals (source-banned by test).
 */
import type { Surface } from "@pv/engine";

export interface CanvasGeometry {
  vertices: { x: number; y: number }[];
  obstacles: { center: { x: number; y: number }; radiusM: number; heightM: number; kind: string }[];
}

export interface CanvasSurface {
  id: string;
  geometry: CanvasGeometry;
  /** Module-GRID rotation (persisted layout property — not the E60 camera). */
  rotationDeg: number;
  edgeSetbackM: number;
  kind: string;
  /** E63.1: the surface's REAL physical properties — the engine keys the
   *  unconditional E55 tilt correction on tiltDeg, so a wrong tilt here is a
   *  wrong module count on screen. */
  tiltDeg: number;
  azimuthDeg: number;
  obstacleBufferM: number;
  /** Layout preview overrides for this surface. */
  removedKeys?: Set<string>;
  movedOffsets?: Record<string, { dx: number; dy: number }>;
}

export function toEngineSurface(s: CanvasSurface): Surface {
  return {
    id: s.id,
    kind: s.kind as Surface["kind"],
    boundary: { vertices: s.geometry.vertices },
    obstacles: s.geometry.obstacles.map((o) => ({
      center: o.center,
      radiusM: o.radiusM,
      heightM: o.heightM,
      kind: o.kind as Surface["obstacles"][number]["kind"],
    })),
    azimuthDeg: s.azimuthDeg,
    tiltDeg: s.tiltDeg,
    edgeSetbackM: s.edgeSetbackM,
    obstacleBufferM: s.obstacleBufferM,
    gridRotationDeg: s.rotationDeg,
  };
}
